/**
 * Community discovery category filters.
 * `filterCategory` is the backend category value; `label` is chip display text.
 */
import {
  EngineeringOutlined,
  CampaignOutlined,
  StorefrontOutlined,
  SettingsOutlined,
  SupportAgentOutlined,
  AccountBalanceOutlined,
  GroupsOutlined,
  AnalyticsOutlined,
  DesignServicesOutlined,
  AssignmentOutlined,
  ScienceOutlined,
  MoreHorizOutlined,
  AutoAwesomeOutlined,
  ShieldOutlined,
} from '@mui/icons-material';
import { DEFAULT_ACCOUNT_CATEGORIES } from './accountCategories';

export const COMMUNITY_CATEGORIES = ['All', ...DEFAULT_ACCOUNT_CATEGORIES];

/** Profile topic interests — same labels as community categories, minus "All". */
export const COMMUNITY_JOB_FUNCTION_OPTIONS = COMMUNITY_CATEGORIES.filter((c) => c !== 'All');

/** Category chips beneath scope tabs (excludes Recommended / All Community tabs). */
export const COMMUNITY_CATEGORY_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'Sales & GTM', label: 'Sales & GTM', filterCategory: 'Sales & GTM', icon: StorefrontOutlined },
  { value: 'Marketing & Content', label: 'Marketing & Content', filterCategory: 'Marketing & Content', icon: CampaignOutlined },
  { value: 'Customer Success & Support', label: 'Customer Success', filterCategory: 'Customer Success & Support', icon: SupportAgentOutlined },
  { value: 'Operations', label: 'Operations', filterCategory: 'Operations', icon: SettingsOutlined },
  { value: 'Product & Design', label: 'Product & Design', filterCategory: 'Product & Design', icon: DesignServicesOutlined },
  { value: 'Engineering & IT', label: 'Engineering & IT', filterCategory: 'Engineering & IT', icon: EngineeringOutlined },
  { value: 'Data & Analytics', label: 'Data & Analytics', filterCategory: 'Data & Analytics', icon: AnalyticsOutlined },
  { value: 'Finance & Legal', label: 'Finance & Legal', filterCategory: 'Finance & Legal', icon: AccountBalanceOutlined },
  { value: 'People & HR', label: 'People & HR', filterCategory: 'People & HR', icon: GroupsOutlined },
  { value: 'Strategy & Planning', label: 'Strategy & Planning', filterCategory: 'Strategy & Planning', icon: AssignmentOutlined },
  { value: 'Research & Learning', label: 'Research & Learning', filterCategory: 'Research & Learning', icon: ScienceOutlined },
  { value: 'Compliance & Risk', label: 'Compliance & Risk', filterCategory: 'Compliance & Risk', icon: ShieldOutlined },
  { value: 'Other', label: 'Other', filterCategory: 'Other', icon: MoreHorizOutlined },
];

const COMMUNITY_CATEGORY_LEGACY_MATCHES = {
  'Sales & GTM': ['Sales', 'Business Development'],
  'Marketing & Content': ['Marketing', 'Content & Writing'],
  'Customer Success & Support': ['Customer Support'],
  'Product & Design': ['Product Management', 'Design & UX'],
  'Engineering & IT': ['Engineering', 'IT & Infrastructure', 'Quality Assurance'],
  'Finance & Legal': ['Accounting & Finance', 'Legal'],
  'People & HR': ['Human Resources'],
  'Strategy & Planning': ['Strategy & Consulting', 'Project Management'],
  'Research & Learning': ['Research & Development'],
};

/** @deprecated Use COMMUNITY_CATEGORY_FILTERS — kept for any legacy imports. */
export const COMMUNITY_DISCOVERY_FILTERS = [
  { value: 'Recommended', label: 'Recommended', icon: AutoAwesomeOutlined },
  { value: 'all', label: 'All' },
  ...COMMUNITY_CATEGORY_FILTERS.filter((f) => f.value !== 'all'),
];

export function resolveCommunityFilterCategory(filterValue) {
  if (!filterValue || filterValue === 'all' || filterValue === 'All') {
    return undefined;
  }
  const match = COMMUNITY_CATEGORY_FILTERS.find((f) => f.value === filterValue);
  return match?.filterCategory || match?.value || filterValue;
}

export function resolveCommunityFilterCategories(filterValue) {
  const primary = resolveCommunityFilterCategory(filterValue);
  if (!primary) return undefined;
  return [primary, ...(COMMUNITY_CATEGORY_LEGACY_MATCHES[primary] || [])];
}

export function parseCommunityRoute(categoryName) {
  if (!categoryName) {
    return { scopeTab: 0, category: 'all' };
  }
  const decoded = decodeURIComponent(categoryName);
  if (decoded === 'All') {
    return { scopeTab: 1, category: 'all' };
  }
  const match = COMMUNITY_CATEGORY_FILTERS.find(
    (f) => f.filterCategory === decoded || f.value === decoded || f.label === decoded
  );
  return { scopeTab: 1, category: match?.value || decoded };
}

export function normalizeCommunityFilterFromRoute(categoryName) {
  return parseCommunityRoute(categoryName).category;
}

export function getCommunityCategoryDisplayLabel(category) {
  if (!category) return '';
  const match = COMMUNITY_CATEGORY_FILTERS.find(
    (f) => f.filterCategory === category || f.value === category
  );
  return match?.label || category;
}
